// frontend/app/register/page.tsx

"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "@/i18n/useTranslation";

export default function RegisterPage() {
    const router = useRouter();
    const { refreshUser } = useAuth();
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState("");

    // Form Data
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");

    const [restaurantName, setRestaurantName] = useState("");
    const [restaurantSlug, setRestaurantSlug] = useState("");

    // Generar slug automático
    useEffect(() => {
        if (restaurantName && !restaurantSlug) {
            const generated = restaurantName
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "-")
                .replace(/-+/g, "-")
                .replace(/^-|-$/g, "");
            setRestaurantSlug(generated);
        }
    }, [restaurantName]);

    // Validar Paso 1
    const validateStep1 = () => {
        if (!email || !password || !confirmPassword || !firstName || !lastName || !phone) {
            setError(t("common.requiredFields") || "Todos los campos son obligatorios");
            return false;
        }
        if (password !== confirmPassword) {
            setError(t("register.passwordMismatch"));
            return false;
        }
        if (password.length < 8) {
            setError(t("profile.passwordRequirements"));
            return false;
        }
        return true;
    };

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (validateStep1()) {
            setStep(2);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        if (!restaurantName || !restaurantSlug) {
            setError(t("common.requiredFields") || "Todos los campos son obligatorios");
            setLoading(false);
            return;
        }

        try {
            const res = await fetch("/api/auth/register", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    password,
                    first_name: firstName,
                    last_name: lastName,
                    phone,
                    restaurant_name: restaurantName,
                    restaurant_slug: restaurantSlug,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                if (data.detail === "El email ya está registrado") {
                    setError(t("register.emailExists"));
                } else if (data.detail === "El slug del restaurante ya existe") {
                    setError(t("register.slugExists"));
                } else {
                    setError(data.detail || t("errors.generic"));
                }
                return;
            }

            // Éxito - mostrar pantalla de verificación
            setRegisteredEmail(email);
            setRegistrationComplete(true);

        } catch (err) {
            setError(t("errors.networkError"));
        } finally {
            setLoading(false);
        }
    };

    // Pantalla de verificación de email
    if (registrationComplete) {
        return (
            <main className="min-h-screen bg-black flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-zinc-900 rounded-xl p-8 border border-zinc-800 shadow-2xl text-center">
                    {/* Icono */}
                    <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-4xl">✉️</span>
                    </div>

                    <h1 className="text-2xl font-bold text-white mb-2">
                        {t("register.checkEmail") || "¡Revisa tu email!"}
                    </h1>

                    <p className="text-zinc-400 mb-6">
                        {t("register.verificationSent") || "Hemos enviado un enlace de verificación a:"}
                    </p>

                    <p className="text-emerald-400 font-medium text-lg mb-8">
                        {registeredEmail}
                    </p>

                    <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 text-left">
                        <p className="text-zinc-300 text-sm">
                            {t("register.verificationInstructions") || "Haz clic en el enlace del email para activar tu cuenta. El enlace expira en 24 horas."}
                        </p>
                    </div>

                    <p className="text-zinc-500 text-sm mb-4">
                        {t("register.noEmail") || "¿No recibiste el email?"}
                    </p>

                    <ResendButton email={registeredEmail} />

                    <div className="mt-8 pt-6 border-t border-zinc-800">
                        <Link href="/login" className="text-zinc-400 hover:text-white text-sm">
                            ← {t("register.backToLogin") || "Volver al login"}
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-black flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-zinc-900 rounded-xl p-8 border border-zinc-800 shadow-2xl">
                <h1 className="text-3xl font-bold mb-2 text-center text-white">
                    {t("register.title")}
                </h1>

                {/* Progress */}
                <div className="flex justify-center gap-2 mb-8">
                    <div className={`h-1 w-16 rounded ${step >= 1 ? "bg-emerald-500" : "bg-zinc-700"}`} />
                    <div className={`h-1 w-16 rounded ${step >= 2 ? "bg-emerald-500" : "bg-zinc-700"}`} />
                </div>

                {error && (
                    <div className="bg-red-900/30 border border-red-900 text-red-400 p-3 rounded text-sm mb-6 text-center">
                        {error}
                    </div>
                )}

                {step === 1 ? (
                    <form onSubmit={handleNext} className="space-y-4">
                        <h2 className="text-xl font-semibold text-zinc-100 mb-4">{t("register.step1Title")}</h2>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs text-zinc-400 mb-1">{t("register.firstName")}</label>
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={e => setFirstName(e.target.value)}
                                    className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-zinc-400 mb-1">{t("register.lastName")}</label>
                                <input
                                    type="text"
                                    value={lastName}
                                    onChange={e => setLastName(e.target.value)}
                                    className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.phone")}</label>
                            <input
                                type="tel"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.email")}</label>
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.password")}</label>
                            <input
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                required
                            />
                            <p className="text-xs text-zinc-500 mt-1">{t("profile.passwordRequirements")}</p>
                        </div>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.confirmPassword")}</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 rounded transition mt-4"
                        >
                            {t("register.next")}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleRegister} className="space-y-6">
                        <h2 className="text-xl font-semibold text-zinc-100 mb-4">{t("register.step2Title")}</h2>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.restaurantName")}</label>
                            <input
                                type="text"
                                value={restaurantName}
                                onChange={e => setRestaurantName(e.target.value)}
                                className="w-full bg-zinc-800 border-zinc-700 rounded p-2 text-sm text-white focus:border-emerald-500 focus:ring-0"
                                required
                                autoFocus
                            />
                        </div>

                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">{t("register.restaurantSlug")}</label>
                            <div className="flex items-center bg-zinc-800 border border-zinc-700 rounded px-2">
                                <span className="text-zinc-500 text-sm select-none">.rezvio.com/</span>
                                <input
                                    type="text"
                                    value={restaurantSlug}
                                    onChange={e => setRestaurantSlug(e.target.value)}
                                    className="w-full bg-transparent border-none focus:ring-0 text-sm text-white p-2 pl-1"
                                    required
                                />
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                                {t("register.slugPreview")} <span className="text-emerald-400">{restaurantSlug}.rezvio.com</span>
                            </p>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 py-2 rounded transition"
                            >
                                {t("common.back")}
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-[2] bg-emerald-600 hover:bg-emerald-500 text-white font-medium py-2 rounded transition disabled:opacity-50"
                            >
                                {loading ? t("common.sending") : t("register.createAccount")}
                            </button>
                        </div>
                    </form>
                )}

                <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
                    <p className="text-zinc-500 text-sm mb-2">{t("register.alreadyHaveAccount")}</p>
                    <Link href="/login" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
                        {t("register.loginLink")}
                    </Link>
                </div>
            </div>
        </main>
    );
}


// Componente para reenviar email
function ResendButton({ email }: { email: string }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    useEffect(() => {
        if (cooldown > 0) {
            const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [cooldown]);

    const handleResend = async () => {
        if (loading || cooldown > 0) return;

        setLoading(true);
        try {
            await fetch("/api/auth/resend-verification", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            setSent(true);
            setCooldown(60); // 60 segundos de cooldown
        } catch (err) {
            // Silenciar error
        } finally {
            setLoading(false);
        }
    };

    if (sent && cooldown > 0) {
        return (
            <p className="text-emerald-400 text-sm">
                ✓ {t("register.emailResent") || "Email reenviado"} ({cooldown}s)
            </p>
        );
    }

    return (
        <button
            onClick={handleResend}
            disabled={loading || cooldown > 0}
            className="text-emerald-400 hover:text-emerald-300 text-sm font-medium disabled:opacity-50"
        >
            {loading ? (t("common.sending") || "Enviando...") : (t("register.resendEmail") || "Reenviar email")}
        </button>
    );
}